import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { BreadcrumbType, Event, NotifiableError, OnErrorCallback } from '@bugsnag/core';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { AuthenticatedUser } from '../../../../http/src/models/authenticated-user';
import { BUG_SNAG_CONFIGURATION, BugsnagConfiguration } from '../../interfaces/bugsnag-configuration';

@Injectable({
  providedIn: 'root'
})
export class BugsnagService {
  // readonly bugsnagClient: Bugsnag.Client;

  constructor(@Inject(BUG_SNAG_CONFIGURATION) protected configuration: BugsnagConfiguration) {
    Bugsnag.start({
      apiKey: this.configuration.bugsnagApiKey,

      // if you track deploys or use source maps, make sure to set the correct version.
      appVersion: this.configuration.appVersion,

      // Bugsnag can track the number of “sessions” that happen in your application, and calculate a crash rate for each release.
      // This defaults to false.
      autoTrackSessions: this.configuration.autoCaptureSessions,

      releaseStage: configuration.releaseStage,

      // defines the release stage for all events that occur in this app.
      // releaseStage: environment.environment,

      //  defines which release stages bugsnag should report. e.g. ignore staging errors.
      enabledReleaseStages: this.configuration.notifyReleaseStages,

      // one of the most powerful tools in our library, onError lets you evaluate, modify,
      // add and remove data on an error right before it is sent to bugsnag.
      // The actions here will be applied to *all* errors, handled and unhandled.
      onError: event => {
        event.errors.forEach(error => {
          if (error.errorClass === 'Error' && event.severity === 'info') {
            error.errorClass = error.errorMessage;
          }
        });

        // note that if you return false from the beforeSend, this will cancel the entire error report.
      },
      metadata: {}
    });
  }

  setUser(user: AuthenticatedUser): void {
    if (user) {
      Bugsnag.setUser(user.userId.toString(), user.firstName + ' ' + user.lastName, user.username);
    } else {
      Bugsnag.setUser(null);
    }
  }

  getErrorHandler(): ErrorHandler {
    if (this.configuration.enableRemoteErrorLogging) {
      return new BugsnagErrorHandler();
    } else {
      return new ErrorHandler();
    }
  }

  notify(error: NotifiableError, onError?: OnErrorCallback, cb?: (err: any, event: Event) => void): void {
    Bugsnag.notify(error, onError, cb);
  }

  leaveBreadcrumb(name: string, metaData?: any, type?: BreadcrumbType): void {
    Bugsnag.leaveBreadcrumb(name, metaData, type);
  }
}
